import { useEffect, useMemo, useState } from 'react'
import {
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Plus, KanbanSquare } from 'lucide-react'
import {
  useProjectStore,
  TASK_STATUS_ORDER,
  type Task,
  type TaskStatus,
} from '../../store/projectStore'
import { usePermissions } from '../../hooks/usePermissions'
import TaskCard from './TaskCard'
import TaskDetailModal from './TaskDetailModal'

const COLUMN_LABELS: Record<TaskStatus, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  review: 'Review',
  done: 'Done',
  blocked: 'Blocked',
}

const COLUMN_BAR: Record<TaskStatus, string> = {
  todo: 'bg-gray-400',
  in_progress: 'bg-blue-500',
  review: 'bg-purple-500',
  done: 'bg-green-500',
  blocked: 'bg-red-500',
}

interface KanbanBoardProps {
  projectId: number
}

interface KanbanColumnProps {
  status: TaskStatus
  tasks: Task[]
  onAddTask: (status: TaskStatus) => void
  onOpenTask: (task: Task) => void
  canCreate: boolean
}

function KanbanColumn({ status, tasks, onAddTask, onOpenTask, canCreate }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: `column:${status}` })
  return (
    <div className="flex w-72 shrink-0 flex-col rounded-lg bg-gray-100 p-3">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${COLUMN_BAR[status]}`} />
          <h3 className="text-sm font-semibold text-gray-700">{COLUMN_LABELS[status]}</h3>
          <span className="rounded-full bg-white px-2 py-0.5 text-[11px] text-gray-500">{tasks.length}</span>
        </div>
        {canCreate && (
          <button
            onClick={() => onAddTask(status)}
            className="rounded p-1 text-gray-500 hover:bg-white hover:text-gray-700"
            aria-label={`Add task to ${COLUMN_LABELS[status]}`}
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>

      <div
        ref={setNodeRef}
        className={`flex-1 min-h-[100px] space-y-2 rounded transition-colors ${isOver ? 'bg-primary-50/60' : ''}`}
      >
        <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {tasks.map((t) => (
            <TaskCard key={t.id} task={t} onOpen={onOpenTask} />
          ))}
        </SortableContext>
      </div>
    </div>
  )
}

interface QuickAddInputProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (title: string) => Promise<void>
  status: TaskStatus
}

function QuickAddInput({ isOpen, onClose, onSubmit, status }: QuickAddInputProps) {
  const [title, setTitle] = useState('')
  const [submitting, setSubmitting] = useState(false)
  if (!isOpen) return null
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    setSubmitting(true)
    try {
      await onSubmit(title.trim())
      setTitle('')
      onClose()
    } finally {
      setSubmitting(false)
    }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={handleSubmit} className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
        <h3 className="mb-2 text-sm font-medium">Add task to {COLUMN_LABELS[status]}</h3>
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Task title"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
        />
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={submitting || !title.trim()}
                  className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
            {submitting ? 'Adding…' : 'Add'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default function KanbanBoard({ projectId }: KanbanBoardProps) {
  const { board, fetchBoard, moveTask, createTask } = useProjectStore()
  const { can } = usePermissions()
  const [activeTask, setActiveTask] = useState<Task | null>(null)
  const [openTask, setOpenTask] = useState<Task | null>(null)
  const [quickAddStatus, setQuickAddStatus] = useState<TaskStatus | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor)
  )

  useEffect(() => {
    fetchBoard(projectId)
  }, [projectId, fetchBoard])

  const columns: Record<TaskStatus, Task[]> = useMemo(() => {
    const empty = TASK_STATUS_ORDER.reduce((acc, s) => ({ ...acc, [s]: [] as Task[] }), {} as Record<TaskStatus, Task[]>)
    return { ...empty, ...(board?.columns || {}) }
  }, [board])

  const canCreate = can('admin_project_monitoring', 'create') || can('pwa_project_monitoring', 'create')
  const canEdit = can('admin_project_monitoring', 'edit') || can('pwa_project_monitoring', 'edit')
  const canDelete = can('admin_project_monitoring', 'delete') || can('pwa_project_monitoring', 'delete')

  const findTask = (id: number): Task | null => {
    for (const s of TASK_STATUS_ORDER) {
      const found = columns[s].find((t) => t.id === id)
      if (found) return found
    }
    return null
  }

  const handleDragStart = (e: DragStartEvent) => {
    const t = findTask(Number(e.active.id))
    setActiveTask(t)
  }

  const handleDragEnd = async (e: DragEndEvent) => {
    setActiveTask(null)
    const { active, over } = e
    if (!over) return

    const taskId = Number(active.id)
    const overId = String(over.id)

    let targetStatus: TaskStatus | null = null
    let beforeTaskId: number | null = null

    if (overId.startsWith('column:')) {
      targetStatus = overId.slice('column:'.length) as TaskStatus
    } else {
      const overTaskId = Number(overId)
      const overTask = findTask(overTaskId)
      if (!overTask) return
      targetStatus = overTask.status
      beforeTaskId = overTaskId
    }

    if (!targetStatus) return

    const colTasks = columns[targetStatus]
    let newPosition: number
    if (beforeTaskId == null) {
      // dropped on column area — place at end
      const max = colTasks.length ? Math.max(...colTasks.map((t) => t.position || 0)) : 0
      newPosition = max + 10
    } else {
      const idx = colTasks.findIndex((t) => t.id === beforeTaskId)
      const before = idx > 0 ? colTasks[idx - 1] : null
      const at = colTasks[idx]
      if (!before) {
        newPosition = (at?.position || 10) - 10
      } else {
        newPosition = Math.floor(((before.position || 0) + (at?.position || 0)) / 2)
        if (newPosition === before.position) newPosition = (at?.position || 0) + 1
      }
    }

    const moving = findTask(taskId)
    if (!moving) return
    if (moving.status === targetStatus && moving.position === newPosition) return

    try {
      await moveTask(taskId, targetStatus, newPosition)
    } catch {
      await fetchBoard(projectId)
    }
  }

  const handleQuickAdd = async (title: string) => {
    if (!quickAddStatus) return
    await createTask(projectId, { title, status: quickAddStatus })
  }

  if (!board) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-12 text-center">
        <KanbanSquare className="mx-auto h-10 w-10 text-gray-400 animate-pulse" />
        <p className="mt-3 text-sm text-gray-500">Loading board…</p>
      </div>
    )
  }

  return (
    <div>
      <DndContext sensors={sensors} collisionDetection={closestCorners}
                  onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4">
          {TASK_STATUS_ORDER.map((s) => (
            <KanbanColumn
              key={s}
              status={s}
              tasks={columns[s]}
              onAddTask={(st) => setQuickAddStatus(st)}
              onOpenTask={setOpenTask}
              canCreate={canCreate}
            />
          ))}
        </div>
        <DragOverlay>
          {activeTask ? (
            <div className="opacity-90">
              <TaskCard task={activeTask} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <QuickAddInput
        isOpen={quickAddStatus !== null}
        status={quickAddStatus || 'todo'}
        onClose={() => setQuickAddStatus(null)}
        onSubmit={handleQuickAdd}
      />

      <TaskDetailModal
        isOpen={openTask !== null}
        task={openTask}
        projectId={projectId}
        canEdit={canEdit}
        canDelete={canDelete}
        onClose={() => setOpenTask(null)}
      />
    </div>
  )
}
